/* eslint-disable no-param-reassign */

import type { UserDataResponse } from "@onestore/api/types"
import { HTTP_STATUS } from "@onestore/onestore-store-common/http"
import type { Draft } from "immer"
import { produce } from "immer"
import { set } from "lodash"
import Storage, { STORAGE } from "~/lib/storage"
import LocalStorage from "~/lib/storage/LocalStorage"
import type { OrderActions } from "~/store/order/actions"
import { OrderActionType } from "~/store/order/constants"
import type { AccountAction } from "../account/actions"
import { AccountActionTypes } from "../account/actions"
import { BonusUpsellActionType } from "../bonus/actions-upsell"
import type { CheckoutAction } from "../checkout/actions"
import { CheckoutActions } from "../checkout/constants"
import type { BasketAction } from "./actions"
import { BASKET_STATUS, BasketActionSource, BasketActions } from "./constants"
import type { BasketState } from "./types"

const basketInfo = LocalStorage.get(STORAGE.BASKET_INFO)

const total_net_price = basketInfo
  ? parseInt(JSON.parse(basketInfo)[STORAGE.BASKET_INFO_NET_PRICE], 10)
  : 0
const items_count = basketInfo
  ? parseInt(JSON.parse(basketInfo)[STORAGE.BASKET_INFO_CNT], 10)
  : 0

export const initialState: BasketState = {
  domains: [],
  plans_usage_over_limit: [],
  items_count,
  pmails: [],
  status: BASKET_STATUS.NONE,
  total_net_price,
  total_vat_value: 0,
  handling_net_value: 0,
  request_state: HTTP_STATUS.NONE,
  token: Storage.getToken(),
  buttons: {},
  items_statuses: {},
  items: [],
  savings: 0,
  promo_code: undefined,
  order_id: null,
  order_number: null,
  messages: [],
  needs_parameters: false,
  get_params_request: HTTP_STATUS.NONE,
  account_type: null,
  userDataReady: false,
  basketReady: false,
  promoCodeInvalid: false,
  domainUpsell: [],
  modal: {
    removeItem: {
      isOpen: false,
      itemId: undefined,
      planId: undefined,
      actionSource: BasketActionSource.EMPTY,
    },
    planOverLimit: {
      isOpen: false,
    },
  },
}

export default function reducer(
  state: BasketState = initialState,
  action: BasketAction | AccountAction | CheckoutAction | OrderActions
): BasketState {
  return produce<BasketState, BasketState>(
    state,
    (nextState: Draft<BasketState>) => {
      switch (action.type) {
        case BasketActions.REMOVE_ITEM_CONFIRM_MODAL_SHOW:
          nextState.modal.removeItem.isOpen = true
          nextState.modal.removeItem.itemId = action.itemId
          nextState.modal.removeItem.planId = action.planId
          nextState.modal.removeItem.actionSource = action.actionSource
          nextState.modal.removeItem.itemRemoval = action.itemRemoval
          nextState.modal.removeItem.content = action.content

          return

        case BasketActions.REMOVE_ITEM_CONFIRM_MODAL_CLOSE:
          nextState.modal.removeItem.isOpen = false
          nextState.modal.removeItem.itemId = undefined
          nextState.modal.removeItem.planId = undefined
          nextState.modal.removeItem.actionSource = BasketActionSource.EMPTY
          nextState.modal.removeItem.itemRemoval = undefined
          nextState.modal.removeItem.content = undefined

          return

        case BasketActions.BASKET_ITEM_REMOVED:
          nextState.modal.removeItem.isOpen = false
          nextState.modal.removeItem.itemId = undefined
          nextState.modal.removeItem.planId = undefined
          nextState.modal.removeItem.actionSource = BasketActionSource.EMPTY
          nextState.modal.removeItem.itemRemoval = undefined

          return

        case BasketActions.BASKET_PREPARE_FOR_SUMMARY:
          nextState.status = initialState.status
          nextState.items = initialState.items
          nextState.total_net_price = initialState.total_net_price
          nextState.total_vat_value = initialState.total_vat_value
          nextState.handling_net_value = initialState.handling_net_value
          nextState.request_state = initialState.request_state
          nextState.items_count = initialState.items_count
          nextState.items_statuses = initialState.items_statuses

          return

        case BasketActions.BASKET:
          if ("buttonId" in action && action.buttonId) {
            set(nextState, ["buttons", action.buttonId], HTTP_STATUS.CONTINUE)
          }

          if ("basketItemsIds" in action && action.basketItemsIds) {
            action.basketItemsIds.forEach((itemId: number) => {
              set(nextState, ["items_statuses", itemId], HTTP_STATUS.CONTINUE)
            })
          }
          nextState.request_state = HTTP_STATUS.CONTINUE
          nextState.status = BASKET_STATUS.PENDING

          return

        case BasketActions.BASKET_SUCCESS:
          if (action.buttonId) {
            nextState.buttons[action.buttonId] = HTTP_STATUS.OK
          }

          if (action.basketItemsIds) {
            action.basketItemsIds.forEach((itemId: number) => {
              set(nextState, ["items_statuses", itemId], HTTP_STATUS.OK)
            })
          }

          if (action.result.items) {
            nextState.items = action.result.items
          }

          const messages = action.result.messages ?? [
            { message: action.result.message ?? "" },
          ]
          nextState.items_statuses = {}
          nextState.domainUpsell = action.result.domain_upsell
          nextState.messages = messages
          nextState.items_count = action.result.items_count
          nextState.plans_usage_over_limit =
            action.result.plans_usage_over_limit
          nextState.request_state = HTTP_STATUS.OK
          nextState.total_net_price = action.result.total_net_price
          nextState.total_vat_value = action.result.total_vat_value
          nextState.handling_net_value = action.result.handling_net_value
          nextState.savings = action.result.savings
          nextState.status = action.result.status
          nextState.promo_code = action.result.promo_code
          nextState.domains = action.result.domains
          nextState.pmails = action.result.pmails
          nextState.order_id = action.result.order_id
          nextState.order_number = action.result.order_number
          nextState.account_type = action.result.account_type
          nextState.needs_parameters = action.result.needs_parameters
          nextState.basketReady = true
          break

        case BasketActions.BASKET_ERROR:
          if (action.buttonId) {
            nextState.buttons[action.buttonId] = action.response_status_code
          }

          nextState.request_state = action.response_status_code

          if (action.basketItemsIds) {
            action.basketItemsIds.forEach((itemId: number) => {
              set(
                nextState,
                ["items_statuses", itemId],
                action.response_status_code
              )
            })
          }
          break

        case BasketActions.BASKET_PROMO_CODE_ERROR:
          nextState.request_state = HTTP_STATUS.BAD_REQUEST
          nextState.promoCodeInvalid = true
          break

        case BasketActions.BASKET_RESET_BUTTON:
          nextState.buttons[action.buttonId] = HTTP_STATUS.NONE
          break

        case BonusUpsellActionType.BONUS_UPSELL_DOMAINS_ADD_TO_BASKET_SUCCESS:
        case BonusUpsellActionType.BONUS_UPSELL_DOMAINS_ADD_TO_BASKET_FAILURE:
        case BonusUpsellActionType.BONUS_UPSELL_DOMAINS_ADD_TO_BASKET_REQUEST:
          nextState.buttons[action.buttonId] = action.status

          break

        case AccountActionTypes.ACTION_ACCOUNT_LOGOUT:
          nextState.status = BASKET_STATUS.ACTIVE
          break

        case CheckoutActions.USER_DATA_SUCCESS:
          const {
            result: {
              basket: {
                total_net_price,
                total_vat_value,
                items_count,
                token,
                domains,
                pmails,
                status,
                items,
                plans_usage_over_limit,
              },
            },
          }: { result: UserDataResponse } = action
          Storage.setToken(token)
          nextState.request_state = HTTP_STATUS.OK
          nextState.total_net_price = total_net_price
          nextState.total_vat_value = total_vat_value
          nextState.items_count = items_count
          nextState.plans_usage_over_limit = plans_usage_over_limit
          nextState.token = token
          nextState.domains = domains
          nextState.pmails = pmails
          nextState.status = status
          nextState.userDataReady = true
          nextState.promoCodeInvalid = false

          if (Array.isArray(items)) {
            nextState.items = items
          }

          return

        case OrderActionType.PAYMENT_SUCCESS:
          nextState.status = BASKET_STATUS.ARCHIVED
          nextState.order_id = action.result.id
          nextState.order_number = action.result.number

          return

        case CheckoutActions.USER_DATA_FAILURE:
          nextState.request_state = HTTP_STATUS.BAD_REQUEST

          return

        case CheckoutActions.USER_DATA_PENDING:
          nextState.request_state = HTTP_STATUS.CONTINUE
          nextState.promoCodeInvalid = false
          // eslint-disable-next-line
          return

        case BasketActions.PLAN_OVER_LIMIT_MODAL_SHOW:
          nextState.modal.planOverLimit.isOpen = true
          return

        case BasketActions.PLAN_OVER_LIMIT_MODAL_CLOSE:
          nextState.modal.planOverLimit.isOpen = false
          return

        default:
          return
      }
    }
  )
}
